import React, { useEffect, useState, useCallback } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Layout } from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { getApiUrl } from "../config/api";
import {
  CreditCardIcon,
  TagIcon,
  UserPlusIcon,
  UsersIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BanknotesIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlusIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
  CalendarDaysIcon,
  EyeIcon,
  ClipboardIcon,
  ClipboardDocumentCheckIcon,
  MagnifyingGlassIcon,
  FunnelIcon
} from "@heroicons/react/24/outline";

interface PlanRow {
  id: string;
  code: string;
  name: string;
  durationDays: number;
  isActive: boolean;
  price?: number;
  maxStores?: number;
}

interface PromoRow {
  id: string;
  code: string;
  planId: string | null;
  durationDays: number | null;
  expiresAt: string | null;
  maxUses: number | null;
  usedCount: number;
  isActive: boolean;
  deletedAt?: string | null;
}

interface SubscriptionRow {
  id: string;
  userId: string;
  userEmail: string | null;
  planId: string | null;
  planName: string | null;
  status: "inactive" | "active" | "expired";
  startAt: string | null;
  endAt: string | null;
}

export const AdminBilling: React.FC = () => {
  const { authFetch } = useAuth();
  const [plans, setPlans] = useState<PlanRow[]>([]);
  const [promos, setPromos] = useState<PromoRow[]>([]);
  const [users, setUsers] = useState<Array<{ id: string; email: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subs, setSubs] = useState<SubscriptionRow[]>([]);
  const [creating, setCreating] = useState(false);
  const [newPromoPlanId, setNewPromoPlanId] = useState<string>("");
  const [newPromoMaxUses, setNewPromoMaxUses] = useState<number>(1);
  const [newPromoExpiresAt, setNewPromoExpiresAt] = useState<Date | null>(null);
  const [granting, setGranting] = useState(false);
  const [grantUserId, setGrantUserId] = useState<string>("");
  const [grantPlanId, setGrantPlanId] = useState<string>("");
  // Create plan form
  const [isCreatingPlan, setIsCreatingPlan] = useState(false);
  const [planName, setPlanName] = useState<string>("");
  const [planMonths, setPlanMonths] = useState<number>(1);
  const [planMaxStores, setPlanMaxStores] = useState<number>(1);
  const [planPrice, setPlanPrice] = useState<number>(0);
  const [planError, setPlanError] = useState<string | null>(null);
  const [copiedIds, setCopiedIds] = useState<Set<string>>(new Set());
  const [customDays, setCustomDays] = useState<{ [key: string]: number }>({});
  
  // Состояние для фильтров подписок
  const [filters, setFilters] = useState({
    status: "all" as "all" | "active" | "expired" | "inactive",
    daysLeft: "all" as "all" | "expiring" | "warning" | "healthy",
    search: ""
  });

  // Функция для копирования ID в буфер обмена
  const copyToClipboard = async (id: string, type: 'user' | 'subscription' = 'user') => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedIds(prev => new Set(prev).add(id));
      // Убираем индикатор копирования через 2 секунды
      setTimeout(() => {
        setCopiedIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      }, 2000);
    } catch (err) {
      console.error('Ошибка копирования:', err);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [plansRes, promosRes, subsRes, usersRes] = await Promise.all([
        authFetch(getApiUrl("/api/admin/billing/plans")),
        authFetch(getApiUrl("/api/admin/billing/promos")),
        authFetch(getApiUrl("/api/admin/billing/subscriptions")),
        authFetch(getApiUrl("/api/admin/users")),
      ]);

      if (!plansRes.ok || !promosRes.ok || !subsRes.ok || !usersRes.ok) {
        const msg = `Ошибка загрузки: ${[plansRes, promosRes, subsRes, usersRes]
          .map((r) => (r.ok ? "ok" : r.status))
          .join(", ")}`;
        throw new Error(msg);
      }

      const plansData = await plansRes.json();
      const promosData = await promosRes.json();
      const subsData = await subsRes.json();
      const usersData = await usersRes.json();
      setPlans(Array.isArray(plansData) ? plansData : []);
      setPromos(Array.isArray(promosData) ? promosData : []);
      setSubs(Array.isArray(subsData) ? subsData : []);
      setUsers(
        Array.isArray(usersData)
          ? (usersData as any[]).map((u) => ({ id: u.id, email: u.email }))
          : []
      );
      // не выбираем тариф автоматически, чтобы избежать ошибок выдачи
      setGrantPlanId("");
    } catch (e: any) {
      setError(e?.message || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const togglePlan = async (p: PlanRow) => {
    await authFetch(getApiUrl(`/api/admin/billing/plans/${p.id}`), {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ isActive: !p.isActive }),
    });
    fetchData();
  };

  const togglePromo = async (pr: PromoRow) => {
    await authFetch(getApiUrl(`/api/admin/billing/promos/${pr.id}`), {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ isActive: !pr.isActive }),
    });
    fetchData();
  };

  const createPromo = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      const res = await authFetch(getApiUrl(`/api/admin/billing/promos`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          planId: newPromoPlanId || undefined,
          maxUses: newPromoMaxUses || 1,
          expiresAt: newPromoExpiresAt ? newPromoExpiresAt.toISOString() : null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.message || "Не удалось создать промокод");
      }
      setNewPromoPlanId("");
      setNewPromoMaxUses(1);
      setNewPromoExpiresAt(null);
      await fetchData();
    } catch (e: any) {
      setError(e?.message || "Ошибка создания промокода");
    } finally {
      setCreating(false);
    }
  };

  const createPlan = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreatingPlan(true);
    setPlanError(null);
    try {
      const res = await authFetch(getApiUrl("/api/admin/billing/plans"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: planName || undefined,
          months: planMonths,
          maxStores: planMaxStores,
          price: planPrice,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.message || "Не удалось создать тариф");
      }
      setPlanName("");
      setPlanMonths(1);
      setPlanMaxStores(1);
      setPlanPrice(0);
      await fetchData();
    } catch (e: any) {
      setPlanError(e?.message || "Ошибка создания тарифа");
    } finally {
      setIsCreatingPlan(false);
    }
  };

  // Вычисляем статистики
  const activeSubscriptions = subs.filter(s => s.status === "active").length;
  const totalPlans = plans.length;
  const activePlans = plans.filter(p => p.isActive).length;
  const totalPromos = promos.length;
  const activePromos = promos.filter(p => p.isActive).length;
  const totalRevenue = subs.reduce((acc, sub) => {
    const plan = plans.find(p => p.id === sub.planId);
    return acc + (plan?.price || 0);
  }, 0);

  const stats = [
    {
      name: "Активные подписки",
      value: activeSubscriptions.toString(),
      icon: UsersIcon,
      description: `Из ${subs.length} общих`,
      color: "blue",
      trend: activeSubscriptions > 0 ? "up" : "neutral",
    },
    {
      name: "Активные тарифы",
      value: `${activePlans}/${totalPlans}`,
      icon: CreditCardIcon,
      description: "Доступные тарифы",
      color: "green",
      trend: "up",
    },
    {
      name: "Промокоды",
      value: `${activePromos}/${totalPromos}`,
      icon: TagIcon,
      description: "Активные промокоды",
      color: "purple",
      trend: activePromos > 0 ? "up" : "neutral",
    },
    {
      name: "Общий доход",
      value: `${totalRevenue.toLocaleString()} ₽`,
      icon: BanknotesIcon,
      description: "От активных подписок",
      color: "emerald",
      trend: totalRevenue > 0 ? "up" : "neutral",
    },
  ];

  const getColorClasses = (color: string) => {
    const colorMap = {
      blue: {
        bg: "from-blue-50 to-blue-100",
        border: "border-blue-200",
        icon: "text-blue-600",
        text: "text-blue-700",
        value: "text-blue-900",
      },
      green: {
        bg: "from-green-50 to-green-100",
        border: "border-green-200",
        icon: "text-green-600",
        text: "text-green-700",
        value: "text-green-900",
      },
      purple: {
        bg: "from-purple-50 to-purple-100",
        border: "border-purple-200",
        icon: "text-purple-600",
        text: "text-purple-700",
        value: "text-purple-900",
      },
      emerald: {
        bg: "from-emerald-50 to-emerald-100",
        border: "border-emerald-200",
        icon: "text-emerald-600",
        text: "text-emerald-700",
        value: "text-emerald-900",
      },
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };

  const getTrendIcon = (trend: string) => {
    if (trend === "up") return <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />;
    if (trend === "down") return <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />;
    return null;
  };

  return (
    <Layout>
      <style>
        {`
          /* Стили для принудительного открытия выпадающих списков под селектом */
          .subscription-select {
            position: relative;
            z-index: 10;
            /* Принудительное позиционирование выпадающего списка */
            overflow: visible !important;
          }
          
          .subscription-select:focus {
            z-index: 20;
          }
          
          /* Контейнер для селекта с правильным позиционированием */
          .select-container {
            position: relative;
            display: block;
            overflow: visible;
          }
          
          /* Принудительное позиционирование выпадающего списка под селектом */
          .subscription-select {
            /* Убираем любые ограничения по высоте */
            max-height: none !important;
            overflow-y: visible !important;
            
            /* Принудительная установка направления */
            direction: ltr !important;
            
            /* Убираем clip и содержание */
            clip: auto !important;
            clip-path: none !important;
            
            /* Позиционирование */
            transform: none !important;
            top: auto !important;
            bottom: auto !important;
          }
          
          /* Стили опций */
          .subscription-select option {
            padding: 8px 12px;
            background-color: white;
            color: #374151;
            line-height: 1.5;
            min-height: 2rem;
            display: block;
          }
          
          .subscription-select option:hover {
            background-color: #F3F4F6;
          }
          
          .subscription-select option:checked,
          .subscription-select option:selected {
            background-color: #EEF2FF;
            color: #4F46E5;
            font-weight: 500;
          }
          
          /* Дополнительные правила для принудительного позиционирования */
          .subscription-select::-webkit-scrollbar {
            width: 8px;
          }
          
          .subscription-select::-webkit-scrollbar-track {
            background: #f1f1f1;
          }
          
          .subscription-select::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 4px;
          }
        `}
      </style>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center gap-2">
          <BanknotesIcon className="h-7 w-7 text-indigo-600" />
          <h1 className="text-2xl font-bold text-gray-900">Управление биллингом</h1>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            <div className="flex items-center">
              <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
              {error}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <span className="ml-3 text-gray-500">Загрузка данных...</span>
          </div>
        ) : (
          <>
            {/* Stats Overview */}
            <div>
              <div className="flex items-center gap-2 mb-6">
                <EyeIcon className="h-6 w-6 text-indigo-600" />
                <h2 className="text-xl font-semibold text-gray-900">Обзор биллинга</h2>
              </div>
              <div className="card">
                <div className="px-6 py-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                    {stats.map((item) => (
                      <div key={item.name} className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-500 h-10 flex items-start pt-1 leading-tight">{item.name}</div>
                            <div className="text-2xl font-bold text-gray-900">{item.value}</div>
                            <div className="text-xs text-gray-500 mt-1">{item.description}</div>
                          </div>
                          <div className="flex-shrink-0 ml-3">
                            <item.icon className={`h-6 w-6 ${getColorClasses(item.color).icon}`} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            {/* Тарифы */}
            <div className="xl:col-span-2">
              <div className="flex items-center gap-2 mb-6">
                <CreditCardIcon className="h-6 w-6 text-indigo-600" />
                <h2 className="text-xl font-semibold text-gray-900">Управление тарифами</h2>
              </div>
              <div className="card">
                <div className="px-6 py-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Создать новый тариф</h3>
                    <button className="btn-secondary" onClick={fetchData}>
                      <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
                      Обновить
                    </button>
                  </div>
                  <form onSubmit={createPlan} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="lg:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <ShoppingCartIcon className="h-4 w-4 inline mr-1" />
                          Название тарифа
                        </label>
                        <input
                          className="input-field"
                          type="text"
                          placeholder="Например: Бизнес 5 мес (10 магазинов)"
                          value={planName}
                          onChange={(e) => setPlanName(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <CalendarDaysIcon className="h-4 w-4 inline mr-1" />
                          Месяцев
                        </label>
                        <input
                          className="input-field"
                          type="number"
                          min={1}
                          value={planMonths}
                          onChange={(e) =>
                            setPlanMonths(
                              Math.max(1, parseInt(e.target.value || "1", 10))
                            )
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <CurrencyDollarIcon className="h-4 w-4 inline mr-1" />
                          Цена, ₽
                        </label>
                        <input
                          className="input-field"
                          type="number"
                          min={0}
                          value={planPrice}
                          onChange={(e) =>
                            setPlanPrice(
                              Math.max(0, parseInt(e.target.value || "0", 10))
                            )
                          }
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <UsersIcon className="h-4 w-4 inline mr-1" />
                          Макс. магазинов
                        </label>
                        <input
                          className="input-field"
                          type="number"
                          min={1}
                          value={planMaxStores}
                          onChange={(e) =>
                            setPlanMaxStores(
                              Math.max(1, parseInt(e.target.value || "1", 10))
                            )
                          }
                        />
                      </div>
                      <div className="flex items-end">
                        <button
                          className="btn-primary w-full"
                          type="submit"
                          disabled={isCreatingPlan}
                        >
                          {isCreatingPlan ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Создание...
                            </>
                          ) : (
                            <>
                              <PlusIcon className="h-4 w-4 mr-2" />
                              Создать тариф
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    {planError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                        <div className="flex items-center">
                          <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                          {planError}
                        </div>
                      </div>
                    )}
                  </form>
                  
                  <div className="mt-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Существующие тарифы</h3>
                    {plans.length === 0 ? (
                      <div className="text-center py-8">
                        <CreditCardIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Нет созданных тарифов</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {plans.map((p) => (
                          <div
                            key={p.id}
                            className={`relative bg-white rounded-lg border-2 transition-all duration-200 hover:shadow-lg ${
                              p.isActive 
                                ? 'border-green-200 bg-green-50' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="p-4">
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <h4 className="font-semibold text-gray-900 mb-1">{p.name}</h4>
                                  <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span className="bg-gray-100 px-2 py-1 rounded">{p.code}</span>
                                    <span>•</span>
                                    <span>{p.durationDays} дней</span>
                                  </div>
                                </div>
                                <div className={`flex items-center justify-center w-6 h-6 rounded-full ${
                                  p.isActive 
                                    ? 'bg-green-100 text-green-600' 
                                    : 'bg-gray-100 text-gray-400'
                                }`}>
                                  {p.isActive ? (
                                    <CheckCircleIcon className="h-4 w-4" />
                                  ) : (
                                    <XCircleIcon className="h-4 w-4" />
                                  )}
                                </div>
                              </div>

                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-gray-600">Цена:</span>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="number"
                                      className="input-field w-20 text-sm"
                                      min={0}
                                      defaultValue={p.price ?? 0}
                                      onBlur={async (e) => {
                                        const newPrice = parseInt(
                                          e.currentTarget.value || "0",
                                          10
                                        );
                                        if (Number.isNaN(newPrice)) return;
                                        await authFetch(
                                          getApiUrl(`/api/admin/billing/plans/${p.id}`),
                                          {
                                            method: "PATCH",
                                            headers: { "Content-Type": "application/json" },
                                            body: JSON.stringify({ price: newPrice }),
                                          }
                                        );
                                        fetchData();
                                      }}
                                    />
                                    <span className="text-sm text-gray-500">₽</span>
                                  </div>
                                </div>

                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-gray-600">Макс. магазинов:</span>
                                  <span className="font-medium text-gray-900">
                                    {typeof p.maxStores === "number" ? p.maxStores : 1}
                                  </span>
                                </div>
                              </div>

                              <div className="mt-4 pt-3 border-t border-gray-100">
                                <button
                                  className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                                    p.isActive
                                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                                  }`}
                                  onClick={() => togglePlan(p)}
                                >
                                  {p.isActive ? 'Отключить тариф' : 'Включить тариф'}
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            </div>
            {/* Промокоды */}
            <div className="xl:col-span-2">
              <div className="flex items-center gap-2 mb-6">
                <TagIcon className="h-6 w-6 text-indigo-600" />
                <h2 className="text-xl font-semibold text-gray-900">Управление промокодами</h2>
              </div>
              <div className="card">
                <div className="px-6 py-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Создать новый промокод</h3>
                    <button className="btn-secondary" onClick={fetchData}>
                      <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
                      Обновить
                    </button>
                  </div>
              <form onSubmit={createPromo} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <CreditCardIcon className="h-4 w-4 inline mr-1" />
                      Тариф
                    </label>
                    <select
                      className="input-field pr-8"
                      style={{ 
                        appearance: 'none',
                        backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 20 20\' fill=\'currentColor\' class=\'w-5 h-5\'%3e%3cpath fill-rule=\'evenodd\' d=\'M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z\' clip-rule=\'evenodd\'/%3e%3c/svg%3e")',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.5rem center',
                        backgroundSize: '1.5em 1.5em'
                      }}
                      value={newPromoPlanId}
                      onChange={(e) => setNewPromoPlanId(e.target.value)}
                    >
                      <option value="">— выберите тариф —</option>
                      {plans.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} • {Math.round((p.durationDays || 0) / 30)} мес.
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <UsersIcon className="h-4 w-4 inline mr-1" />
                      Лимит использований
                    </label>
                    <input
                      className="input-field"
                      type="number"
                      min={1}
                      value={newPromoMaxUses}
                      onChange={(e) =>
                        setNewPromoMaxUses(parseInt(e.target.value || "1", 10))
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <ClockIcon className="h-4 w-4 inline mr-1" />
                      Истекает
                    </label>
                    <DatePicker
                      className="input-field w-full"
                      selected={newPromoExpiresAt}
                      onChange={(date) =>
                        setNewPromoExpiresAt(date as Date | null)
                      }
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      dateFormat="yyyy-MM-dd HH:mm"
                      placeholderText="Выберите дату и время"
                      isClearable
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    className="btn-primary"
                    type="submit"
                    disabled={creating}
                  >
                    {creating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Создание...
                      </>
                    ) : (
                      <>
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Создать промокод
                      </>
                    )}
                  </button>
                </div>
              </form>
                  
                  <div className="mt-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Существующие промокоды</h3>
                    {promos.length === 0 ? (
                      <div className="text-center py-8">
                        <TagIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Нет созданных промокодов</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {promos.map((pr) => {
                          const usagePercentage = pr.maxUses ? (pr.usedCount / pr.maxUses) * 100 : 0;
                          const isExpired = pr.expiresAt && new Date(pr.expiresAt) < new Date();
                          return (
                            <div
                              key={pr.id}
                              className={`relative bg-white rounded-lg border-2 transition-all duration-200 hover:shadow-lg ${
                                pr.isActive && !isExpired
                                  ? 'border-purple-200 bg-purple-50' 
                                  : isExpired
                                  ? 'border-red-200 bg-red-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="p-4">
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex-1">
                                    <h4 className="font-semibold text-gray-900 mb-1 font-mono">{pr.code}</h4>
                                    <div className="flex items-center gap-2 text-xs">
                                      <span className={`px-2 py-1 rounded ${
                                        pr.isActive && !isExpired
                                          ? 'bg-green-100 text-green-700'
                                          : isExpired
                                          ? 'bg-red-100 text-red-700'
                                          : 'bg-gray-100 text-gray-700'
                                      }`}>
                                        {isExpired ? 'Истёк' : pr.isActive ? 'Активен' : 'Неактивен'}
                                      </span>
                                    </div>
                                  </div>
                                  <div className={`flex items-center justify-center w-6 h-6 rounded-full ${
                                    pr.isActive && !isExpired
                                      ? 'bg-purple-100 text-purple-600' 
                                      : isExpired
                                      ? 'bg-red-100 text-red-600'
                                      : 'bg-gray-100 text-gray-400'
                                  }`}>
                                    {pr.isActive && !isExpired ? (
                                      <CheckCircleIcon className="h-4 w-4" />
                                    ) : (
                                      <XCircleIcon className="h-4 w-4" />
                                    )}
                                  </div>
                                </div>

                                <div className="space-y-3">
                                  <div>
                                    <div className="flex items-center justify-between text-sm">
                                      <span className="text-gray-600">Использовано:</span>
                                      <span className="font-medium">
                                        {pr.usedCount}{pr.maxUses ? ` / ${pr.maxUses}` : ''}
                                      </span>
                                    </div>
                                    {pr.maxUses && (
                                      <div className="mt-1">
                                        <div className="bg-gray-200 rounded-full h-2">
                                          <div 
                                            className={`h-2 rounded-full transition-all duration-300 ${
                                              usagePercentage >= 90 ? 'bg-red-500' :
                                              usagePercentage >= 70 ? 'bg-yellow-500' :
                                              'bg-green-500'
                                            }`}
                                            style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                                          ></div>
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  {pr.expiresAt && (
                                    <div className="flex items-center justify-between text-sm">
                                      <span className="text-gray-600">Истекает:</span>
                                      <span className={`text-xs ${isExpired ? 'text-red-600' : 'text-gray-900'}`}>
                                        {new Date(pr.expiresAt).toLocaleDateString()}
                                      </span>
                                    </div>
                                  )}
                                </div>

                                <div className="mt-4 pt-3 border-t border-gray-100 flex gap-2">
                                  <button
                                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                                      pr.isActive
                                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                                    }`}
                                    onClick={() => togglePromo(pr)}
                                    disabled={!!isExpired}
                                  >
                                    {pr.isActive ? 'Отключить' : 'Включить'}
                                  </button>
                                  <button
                                    className="px-3 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors duration-200"
                                    onClick={async () => {
                                      if (
                                        pr.usedCount > 0 &&
                                        !window.confirm(
                                          "Этот промокод уже использовался. Удалить (скрыть) его?"
                                        )
                                      ) {
                                        return;
                                      }
                                      await authFetch(
                                        getApiUrl(`/api/admin/billing/promos/${pr.id}`),
                                        { method: "DELETE" }
                                      );
                                      fetchData();
                                    }}
                                  >
                                    Удалить
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Управление подписками */}
            <div className="xl:col-span-2">
              <div className="flex items-center gap-2 mb-6">
                <UserPlusIcon className="h-6 w-6 text-indigo-600" />
                <h2 className="text-xl font-semibold text-gray-900">Управление подписками</h2>
              </div>
              
              {/* Выдача подписки */}
              <div className="card mb-6">
                <div className="px-6 py-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Выдать подписку пользователю</h3>
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      setGranting(true);
                      setError(null);
                      try {
                        const plan = plans.find((p) => p.id === grantPlanId);
                        if (!plan) throw new Error("План не найден");
                        if (!grantUserId) throw new Error("Выберите пользователя");
                        const res = await authFetch(
                          getApiUrl(`/api/admin/billing/activate/${grantUserId}`),
                          {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                              planId: plan.id,
                              durationDays: plan.durationDays,
                            }),
                          }
                        );
                        if (!res.ok) {
                          const data = await res.json().catch(() => ({}));
                          throw new Error(
                            data?.message || "Не удалось выдать подписку"
                          );
                        }
                        setGrantUserId("");
                        setGrantPlanId("");
                        await fetchData();
                      } catch (e: any) {
                        setError(e?.message || "Ошибка выдачи подписки");
                      } finally {
                        setGranting(false);
                      }
                    }}
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <UsersIcon className="h-4 w-4 inline mr-1" />
                          Пользователь
                        </label>
                        <div className="select-container">
                          <select
                            className="input-field pr-8 subscription-select"
                            style={{ 
                              appearance: 'none',
                              backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 20 20\' fill=\'%236B7280\' class=\'w-5 h-5\'%3e%3cpath fill-rule=\'evenodd\' d=\'M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z\' clip-rule=\'evenodd\'/%3e%3c/svg%3e")',
                              backgroundRepeat: 'no-repeat',
                              backgroundPosition: 'right 0.75rem center',
                              backgroundSize: '1.25em 1.25em',
                              position: 'relative',
                              zIndex: 1
                            }}
                            value={grantUserId}
                            onChange={(e) => setGrantUserId(e.target.value)}
                            size={1}
                          >
                          <option value="">— выберите пользователя —</option>
                          {users
                            .filter((u) => {
                              const now = new Date();
                              const userActive = subs.some(
                                (s) =>
                                  s.userId === u.id &&
                                  s.endAt &&
                                  new Date(s.endAt) > now
                              );
                              return !userActive;
                            })
                            .map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.email}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <CreditCardIcon className="h-4 w-4 inline mr-1" />
                          Тариф
                        </label>
                        <div className="select-container">
                          <select
                            className="input-field pr-8 subscription-select"
                            style={{ 
                              appearance: 'none',
                              backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 20 20\' fill=\'%236B7280\' class=\'w-5 h-5\'%3e%3cpath fill-rule=\'evenodd\' d=\'M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z\' clip-rule=\'evenodd\'/%3e%3c/svg%3e")',
                              backgroundRepeat: 'no-repeat',
                              backgroundPosition: 'right 0.75rem center',
                              backgroundSize: '1.25em 1.25em',
                              position: 'relative',
                              zIndex: 1
                            }}
                            value={grantPlanId}
                            onChange={(e) => setGrantPlanId(e.target.value)}
                            size={1}
                          >
                          <option value="">— выберите тариф —</option>
                          {plans.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name} • {Math.round((p.durationDays || 0) / 30)} мес.
                            </option>
                          ))}
                          </select>
                        </div>
                        {grantPlanId && (
                          <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
                            {(() => {
                              const plan = plans.find((x) => x.id === grantPlanId);
                              if (!plan) return null;
                              return `Макс. магазинов: ${typeof plan.maxStores === "number" ? plan.maxStores : 1} • Цена: ${plan.price || 0} ₽`;
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button
                        className="btn-primary"
                        type="submit"
                        disabled={granting}
                      >
                        {granting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Выдача...
                          </>
                        ) : (
                          <>
                            <UserPlusIcon className="h-4 w-4 mr-2" />
                            Выдать подписку
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {/* Список подписок */}
              <div className="card">
                <div className="px-6 py-4">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                      <UserPlusIcon className="h-6 w-6 text-indigo-600" />
                      <h3 className="text-lg font-semibold text-gray-900">Активные подписки</h3>
                      <span className="bg-indigo-100 text-indigo-800 text-sm font-medium px-2.5 py-0.5 rounded-full">
                        {(() => {
                          const filteredSubs = subs.filter((s) => {
                            const isExpired = s.endAt && new Date(s.endAt) < new Date();
                            const daysLeft = s.endAt ? Math.max(0, Math.ceil((new Date(s.endAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))) : 0;
                            
                            if (filters.search && !s.userEmail?.toLowerCase().includes(filters.search.toLowerCase())) {
                              return false;
                            }
                            
                            if (filters.status !== "all") {
                              if (filters.status === "active" && s.status !== "active") return false;
                              if (filters.status === "expired" && s.status !== "expired") return false;
                              if (filters.status === "inactive" && s.status !== "inactive") return false;
                            }
                            
                            if (filters.daysLeft !== "all" && s.status === "active" && !isExpired) {
                              if (filters.daysLeft === "expiring" && daysLeft > 3) return false;
                              if (filters.daysLeft === "warning" && (daysLeft <= 3 || daysLeft > 7)) return false;
                              if (filters.daysLeft === "healthy" && daysLeft <= 7) return false;
                            }
                            
                            return true;
                          });
                          
                          const hasActiveFilters = filters.search || filters.status !== "all" || filters.daysLeft !== "all";
                          return hasActiveFilters ? `${filteredSubs.length} из ${subs.length}` : subs.length;
                        })()}
                      </span>
                    </div>
                    <button className="btn-secondary" onClick={fetchData}>
                      <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
                      Обновить
                    </button>
                  </div>
                  
                  {/* Фильтры подписок */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      {/* Поиск */}
                      <div className="flex-1">
                        <div className="flex flex-col">
                          <label className="text-xs font-medium text-gray-700 mb-1">Поиск</label>
                          <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Поиск по email пользователя..."
                              value={filters.search}
                              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Фильтр по статусу */}
                      <div className="flex flex-col sm:flex-row gap-2">
                        <div className="flex flex-col">
                          <label className="text-xs font-medium text-gray-700 mb-1">Статус</label>
                          <select
                            value={filters.status}
                            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as any }))}
                            className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white"
                            style={{ 
                              appearance: 'none',
                              backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 20 20\' fill=\'%236B7280\' class=\'w-5 h-5\'%3e%3cpath fill-rule=\'evenodd\' d=\'M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z\' clip-rule=\'evenodd\'/%3e%3c/svg%3e")',
                              backgroundRepeat: 'no-repeat',
                              backgroundPosition: 'right 0.5rem center',
                              backgroundSize: '1rem'
                            }}
                          >
                            <option value="all">Все статусы</option>
                            <option value="active">Активные</option>
                            <option value="expired">Истекшие</option>
                            <option value="inactive">Неактивные</option>
                          </select>
                        </div>
                        
                        {/* Фильтр по времени до истечения */}
                        <div className="flex flex-col">
                          <label className="text-xs font-medium text-gray-700 mb-1">Время до истечения</label>
                          <select
                            value={filters.daysLeft}
                            onChange={(e) => setFilters(prev => ({ ...prev, daysLeft: e.target.value as any }))}
                            className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white"
                            style={{ 
                              appearance: 'none',
                              backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 20 20\' fill=\'%236B7280\' class=\'w-5 h-5\'%3e%3cpath fill-rule=\'evenodd\' d=\'M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z\' clip-rule=\'evenodd\'/%3e%3c/svg%3e")',
                              backgroundRepeat: 'no-repeat',
                              backgroundPosition: 'right 0.5rem center',
                              backgroundSize: '1rem'
                            }}
                          >
                            <option value="all">Все периоды</option>
                            <option value="expiring">Истекают (≤3 дня)</option>
                            <option value="warning">Требующие внимания (≤7 дней)</option>
                            <option value="healthy">Стабильные (&gt;7 дней)</option>
                          </select>
                        </div>
                        
                        {/* Кнопка сброса */}
                        <div className="flex flex-col justify-end">
                          <button
                            onClick={() => setFilters({ status: "all", daysLeft: "all", search: "" })}
                            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900 text-sm font-medium rounded-lg border border-gray-300 transition-all duration-200 shadow-sm hover:shadow-md whitespace-nowrap"
                            title="Сбросить все фильтры"
                          >
                            Сбросить фильтры
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {subs.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <UsersIcon className="h-8 w-8 text-gray-400" />
                      </div>
                      <h4 className="text-lg font-medium text-gray-900 mb-1">Нет активных подписок</h4>
                      <p className="text-sm text-gray-500">Подписки появятся здесь после активации</p>
                    </div>
                  ) : (
                    (() => {
                      // Фильтрация подписок
                      const filteredSubs = subs.filter((s) => {
                        const isExpired = s.endAt && new Date(s.endAt) < new Date();
                        const daysLeft = s.endAt ? Math.max(0, Math.ceil((new Date(s.endAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))) : 0;
                        
                        // Фильтр по поиску (email)
                        if (filters.search && !s.userEmail?.toLowerCase().includes(filters.search.toLowerCase())) {
                          return false;
                        }
                        
                        // Фильтр по статусу
                        if (filters.status !== "all") {
                          if (filters.status === "active" && s.status !== "active") return false;
                          if (filters.status === "expired" && s.status !== "expired") return false;
                          if (filters.status === "inactive" && s.status !== "inactive") return false;
                        }
                        
                        // Фильтр по времени до истечения (только для активных)
                        if (filters.daysLeft !== "all" && s.status === "active" && !isExpired) {
                          if (filters.daysLeft === "expiring" && daysLeft > 3) return false;
                          if (filters.daysLeft === "warning" && (daysLeft <= 3 || daysLeft > 7)) return false;
                          if (filters.daysLeft === "healthy" && daysLeft <= 7) return false;
                        }
                        
                        return true;
                      });
                      
                      if (filteredSubs.length === 0) {
                        return (
                          <div className="text-center py-12">
                            <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                              <FunnelIcon className="h-8 w-8 text-gray-400" />
                            </div>
                            <h4 className="text-lg font-medium text-gray-900 mb-1">Подписки не найдены</h4>
                            <p className="text-sm text-gray-500">Попробуйте изменить фильтры поиска</p>
                            <button
                              onClick={() => setFilters({ status: "all", daysLeft: "all", search: "" })}
                              className="mt-3 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                            >
                              Сбросить фильтры
                            </button>
                          </div>
                        );
                      }
                      
                      return (
                        <div className="space-y-4">
                          {filteredSubs.map((s) => {
                        const isExpired = s.endAt && new Date(s.endAt) < new Date();
                        const daysLeft = s.endAt ? Math.max(0, Math.ceil((new Date(s.endAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))) : 0;
                        
                        // Определяем цвет карточки на основе статуса
                        const getStatusConfig = () => {
                          if (s.status === "active" && !isExpired) {
                            if (daysLeft <= 3) return { 
                              badge: "bg-orange-100 text-orange-800 border-orange-200",
                              icon: "text-orange-600",
                              border: "border-orange-200",
                              accent: "bg-orange-500"
                            };
                            if (daysLeft <= 7) return { 
                              badge: "bg-yellow-100 text-yellow-800 border-yellow-200",
                              icon: "text-yellow-600",
                              border: "border-yellow-200",
                              accent: "bg-yellow-500"
                            };
                            return { 
                              badge: "bg-green-100 text-green-800 border-green-200",
                              icon: "text-green-600",
                              border: "border-green-200",
                              accent: "bg-green-500"
                            };
                          }
                          if (s.status === "expired" || isExpired) {
                            return { 
                              badge: "bg-red-100 text-red-800 border-red-200",
                              icon: "text-red-600",
                              border: "border-red-200",
                              accent: "bg-red-500"
                            };
                          }
                          return { 
                            badge: "bg-gray-100 text-gray-800 border-gray-200",
                            icon: "text-gray-600",
                            border: "border-gray-200",
                            accent: "bg-gray-500"
                          };
                        };
                        
                        const statusConfig = getStatusConfig();
                        
                        return (
                          <div
                            key={s.id}
                            className={`bg-white rounded-xl border-2 ${statusConfig.border} shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden`}
                          >
                            {/* Компактный улучшенный заголовок карточки */}
                            <div className={`relative bg-gradient-to-br ${
                              s.status === "active" && !isExpired 
                                ? daysLeft <= 3 
                                  ? 'from-orange-50 via-orange-25 to-red-50' 
                                  : daysLeft <= 7 
                                  ? 'from-yellow-50 via-yellow-25 to-orange-50' 
                                  : 'from-green-50 via-emerald-25 to-green-100'
                                : 'from-red-50 via-red-25 to-red-100'
                            } px-5 py-3 border-b border-gray-200`}>
                              
                              {/* Аккуратная верхняя полоса только для активных подписок с предупреждениями */}
                              {s.status === "active" && !isExpired && daysLeft <= 7 && (
                                <div className={`absolute top-0 left-0 right-0 h-0.5 ${
                                  daysLeft <= 3 
                                    ? 'bg-orange-300' 
                                    : 'bg-yellow-300'
                                }`}></div>
                              )}
                              

                              
                              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-3 sm:space-y-0 w-full lg:w-auto">
                                  {/* Компактный аватар */}
                                  <div className="relative flex-shrink-0">
                                    <div className="w-11 h-11 bg-indigo-500 rounded-lg flex items-center justify-center shadow-md ring-1 ring-white">
                                      <span className="text-sm font-bold text-white">
                                        {s.userEmail ? s.userEmail.charAt(0).toUpperCase() : "?"}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  {/* Компактная информация о пользователе */}
                                  <div className="flex-1 min-w-0">
                                    {/* Email в одной строке */}
                                    {s.userEmail ? (
                                      <button
                                        onClick={() => copyToClipboard(s.userEmail!)}
                                        className="text-left group flex items-center space-x-2 hover:bg-white/60 rounded px-2 py-1 transition-all duration-200"
                                        title="Нажмите чтобы скопировать email пользователя"
                                      >
                                        <h3 className="text-base font-bold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                                          {s.userEmail}
                                        </h3>
                                        {copiedIds.has(s.userEmail) ? (
                                          <ClipboardDocumentCheckIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                                        ) : (
                                          <ClipboardIcon className="h-4 w-4 text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0" />
                                        )}
                                      </button>
                                    ) : (
                                      <h3 className="text-base font-bold text-gray-900 truncate px-2 py-1">
                                        {`User #${s.userId?.slice(0, 8)}`}
                                      </h3>
                                    )}
                                    
                                    {/* Компактные статусы в одной строке */}
                                    <div className="flex flex-wrap lg:flex-nowrap items-center gap-2 mt-1">
                                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${
                                        s.status === "active" ? "bg-green-200 text-green-800 border-green-300" :
                                        s.status === "expired" ? "bg-red-200 text-red-800 border-red-300" :
                                        "bg-gray-200 text-gray-800 border-gray-300"
                                      } border`}>
                                        {s.status === "active" ? "Активна" : s.status === "expired" ? "Истекла" : "⚪ Неактивна"}
                                      </span>
                                      {s.status === "active" && !isExpired && (
                                        <div className={`flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-bold whitespace-nowrap ${
                                          daysLeft <= 3 ? 'bg-red-200 text-red-800 border-red-300' : 
                                          daysLeft <= 7 ? 'bg-orange-200 text-orange-800 border-orange-300' : 
                                          'bg-green-200 text-green-800 border-green-300'
                                        } border`}>
                                          <ClockIcon className="h-4 w-4" />
                                          <span>{daysLeft} {daysLeft === 1 ? 'день' : daysLeft <= 4 ? 'дня' : 'дней'}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Компактные действия в одной строке */}
                                <div className="flex flex-col lg:flex-col xl:flex-row lg:items-center xl:items-center gap-3 w-full lg:w-full xl:w-auto">
                                  {/* Ручной ввод дней */}
                                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full lg:w-full lg:justify-end xl:w-auto xl:justify-start">
                                    <div className="text-sm text-gray-600 font-medium text-center sm:text-left">
                                      Произвольно (+/-30 дней):
                                    </div>
                                    <div className="bg-gradient-to-r from-indigo-200 to-blue-200 rounded-lg p-2 border border-indigo-400 h-[40px] flex items-center space-x-1.5 shadow-md flex-shrink-0">
                                    <input
                                      type="number"
                                      min="-30"
                                      max="30"
                                      placeholder="±дни"
                                      value={customDays[s.id] || ''}
                                      onChange={(e) => {
                                        const value = parseInt(e.target.value) || 0;
                                        const limitedValue = Math.min(Math.max(value, -30), 30);
                                        setCustomDays(prev => ({
                                          ...prev,
                                          [s.id]: limitedValue
                                        }));
                                      }}
                                      className="w-16 px-2 py-1.5 text-xs bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300 shadow-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    />
                                    <button
                                      onClick={async () => {
                                        const days = customDays[s.id];
                                        if (!days || Math.abs(days) <= 0 || Math.abs(days) > 30) return;
                                        
                                        const end = s.endAt ? new Date(s.endAt) : new Date();
                                        end.setDate(end.getDate() + days);
                                        await authFetch(
                                          getApiUrl(`/api/admin/billing/subscriptions/${s.id}`),
                                          {
                                            method: "PATCH",
                                            headers: { "Content-Type": "application/json" },
                                            body: JSON.stringify({ 
                                              endAt: end.toISOString(),
                                              status: "active",
                                            }),
                                          }
                                        );
                                        setCustomDays(prev => ({ ...prev, [s.id]: 0 }));
                                        fetchData();
                                      }}
                                      disabled={!customDays[s.id] || Math.abs(customDays[s.id]) <= 0 || Math.abs(customDays[s.id]) > 30}
                                      className="ml-1.5 px-3 py-1.5 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white text-xs font-medium rounded-md transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 disabled:transform-none"
                                      title="Изменить подписку на указанное количество дней (+ увеличить, - уменьшить)"
                                    >
                                      <PlusIcon className="h-3 w-3" />
                                    </button>
                                    </div>
                                  </div>
                                  
                                  {/* Стандартные кнопки */}
                                  <div className="flex flex-wrap sm:flex-nowrap lg:flex-wrap xl:flex-nowrap gap-2 w-full lg:w-full xl:w-auto">
                                  <button
                                    onClick={async () => {
                                      const end = s.endAt ? new Date(s.endAt) : new Date();
                                      end.setDate(end.getDate() + 30);
                                      await authFetch(
                                        getApiUrl(`/api/admin/billing/subscriptions/${s.id}`),
                                        {
                                          method: "PATCH",
                                          headers: { "Content-Type": "application/json" },
                                          body: JSON.stringify({ 
                                            endAt: end.toISOString(),
                                            status: "active",
                                          }),
                                        }
                                      );
                                      fetchData();
                                    }}
                                    className="flex items-center justify-center gap-1.5 px-3 sm:px-4 lg:px-3 xl:px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-xs sm:text-sm lg:text-xs xl:text-sm font-semibold rounded-lg transition-all duration-200 shadow-sm hover:shadow-md flex-1 sm:flex-initial lg:flex-1 xl:flex-initial"
                                    title="Продлить на 30 дней"
                                  >
                                    <PlusIcon className="h-4 w-4" />
                                    30 дней
                                  </button>
                                  <button
                                    onClick={async () => {
                                      const end = s.endAt ? new Date(s.endAt) : new Date();
                                      end.setDate(end.getDate() + 90);
                                      await authFetch(
                                        getApiUrl(`/api/admin/billing/subscriptions/${s.id}`),
                                        {
                                          method: "PATCH",
                                          headers: { "Content-Type": "application/json" },
                                          body: JSON.stringify({ 
                                            endAt: end.toISOString(),
                                            status: "active",
                                          }),
                                        }
                                      );
                                      fetchData();
                                    }}
                                    className="flex items-center justify-center gap-1.5 px-3 sm:px-4 lg:px-3 xl:px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white text-xs sm:text-sm lg:text-xs xl:text-sm font-semibold rounded-lg transition-all duration-200 shadow-sm hover:shadow-md flex-1 sm:flex-initial lg:flex-1 xl:flex-initial"
                                    title="Продлить на 90 дней"
                                  >
                                    <PlusIcon className="h-4 w-4" />
                                    90 дней
                                  </button>
                                  
                                  <button
                                    onClick={async () => {
                                      await authFetch(
                                        getApiUrl(`/api/admin/billing/subscriptions/${s.id}`),
                                        {
                                          method: "PATCH",
                                          headers: { "Content-Type": "application/json" },
                                          body: JSON.stringify({ 
                                            status: "expired",
                                            endAt: new Date().toISOString()
                                          }),
                                        }
                                      );
                                      fetchData();
                                    }}
                                    className="flex items-center justify-center px-3 sm:px-4 lg:px-3 xl:px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-xs sm:text-sm lg:text-xs xl:text-sm font-semibold rounded-lg transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex-1 sm:flex-initial lg:flex-1 xl:flex-initial"
                                    title="Завершить подписку"
                                    disabled={!!isExpired}
                                  >
                                    Завершить
                                  </button>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Основная информация в виде сетки */}
                            <div className="px-6 py-5 bg-white">
                              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
                                
                                {/* Информация о тарифе */}
                                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                  <div className="flex items-center space-x-2 mb-2">
                                    <div className={`w-6 h-6 ${statusConfig.accent} rounded flex items-center justify-center`}>
                                      <CreditCardIcon className="h-4 w-4 text-white" />
                                    </div>
                                    <h4 className="text-xs font-semibold text-gray-900 uppercase tracking-wider">Тарифный план</h4>
                                  </div>
                                  <div>
                                    <p className="text-base font-semibold text-gray-900">
                                      {s.planName ? s.planName.split('(')[0].trim() : "Не указан"}
                                    </p>
                                    {s.planName && s.planName.includes('(') && (
                                      <p className="text-sm text-gray-500 mt-0.5">
                                        ({s.planName.split('(')[1]}
                                      </p>
                                    )}
                                  </div>
                                </div>

                                {/* Срок действия */}
                                <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                                  <div className="flex items-center space-x-2 mb-2">
                                    <div className="w-6 h-6 bg-amber-500 rounded flex items-center justify-center">
                                      <ClockIcon className="h-4 w-4 text-white" />
                                    </div>
                                    <h4 className="text-xs font-semibold text-gray-900 uppercase tracking-wider">Действует до</h4>
                                  </div>
                                  {s.endAt ? (
                                    <div>
                                      <p className="text-base font-semibold text-gray-900">
                                        {new Date(s.endAt).toLocaleDateString('ru-RU', { 
                                          day: 'numeric', 
                                          month: 'long', 
                                          year: 'numeric' 
                                        })}
                                      </p>
                                      <p className="text-sm text-gray-500 mt-0.5">
                                        в {new Date(s.endAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                      </p>
                                    </div>
                                  ) : (
                                    <p className="text-base text-gray-500 italic">Дата не указана</p>
                                  )}
                                </div>

                                {/* ID пользователя */}
                                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                                  <div className="flex items-center space-x-2 mb-2">
                                    <div className="w-6 h-6 bg-blue-500 rounded flex items-center justify-center">
                                      <UsersIcon className="h-4 w-4 text-white" />
                                    </div>
                                    <h4 className="text-xs font-semibold text-gray-900 uppercase tracking-wider">ID пользователя</h4>
                                  </div>
                                  {s.userId ? (
                                    <button
                                      onClick={() => copyToClipboard(s.userId)}
                                      className="text-left group w-full"
                                      title="Нажмите чтобы скопировать ID пользователя"
                                    >
                                      <div className="relative text-xs font-mono text-gray-900 break-all bg-white rounded border p-2 group-hover:bg-blue-100 transition-colors">
                                        {s.userId}
                                        <div className="absolute bottom-1 right-1">
                                          {copiedIds.has(s.userId) ? (
                                            <ClipboardDocumentCheckIcon className="h-3 w-3 text-green-600" />
                                          ) : (
                                            <ClipboardIcon className="h-3 w-3 text-gray-400 group-hover:text-blue-600 transition-colors" />
                                          )}
                                        </div>
                                      </div>
                                    </button>
                                  ) : (
                                    <p className="text-sm text-gray-500 italic">ID не указан</p>
                                  )}
                                </div>

                                {/* ID подписки */}
                                <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                                  <div className="flex items-center space-x-2 mb-2">
                                    <div className="w-6 h-6 bg-purple-500 rounded flex items-center justify-center">
                                      <EyeIcon className="h-4 w-4 text-white" />
                                    </div>
                                    <h4 className="text-xs font-semibold text-gray-900 uppercase tracking-wider">ID подписки</h4>
                                  </div>
                                  <button
                                    onClick={() => copyToClipboard(s.id, 'subscription')}
                                    className="text-left group w-full"
                                    title="Нажмите чтобы скопировать ID подписки"
                                  >
                                    <div className="relative text-xs font-mono text-gray-900 break-all bg-white rounded border p-2 group-hover:bg-purple-100 transition-colors">
                                      {s.id}
                                      <div className="absolute bottom-1 right-1">
                                        {copiedIds.has(s.id) ? (
                                          <ClipboardDocumentCheckIcon className="h-3 w-3 text-green-600" />
                                        ) : (
                                          <ClipboardIcon className="h-3 w-3 text-gray-400 group-hover:text-purple-600 transition-colors" />
                                        )}
                                      </div>
                                    </div>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                          })}
                        </div>
                      );
                    })()
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default AdminBilling;
